import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, ArrowRight, Phone } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Logo } from "@/components/Logo";
import { BlogLangSwitcher } from "@/components/BlogLangSwitcher";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n";
import { setSEO } from "@/lib/seo";
import { getArticle, articles, trArticle } from "@/data/blog";

export default function BlogArticle() {
  const { slug } = useParams();
  const { lang } = useLang();
  const article = getArticle(slug);
  const at = article ? trArticle(article, lang) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (at) setSEO({ title: `${at.title} — I-LEASE SRL`, description: at.excerpt, path: `/blog/${article.slug}`, image: article.cover });
  }, [at]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="blog-not-found">
        <p className="text-muted-foreground">Article introuvable.</p>
        <Link to="/blog" className="text-accent hover:underline">← Retour au blog</Link>
      </div>
    );
  }

  const locale = lang === "nl" ? "nl-BE" : lang === "en" ? "en-GB" : "fr-BE";
  const inLang = lang === "nl" ? "nl-BE" : lang === "en" ? "en-GB" : "fr-BE";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: at.title,
    description: at.excerpt,
    image: article.cover,
    datePublished: article.date,
    keywords: at.keywords,
    inLanguage: inLang,
    author: { "@type": "Organization", name: "I-LEASE SRL" },
    publisher: { "@type": "Organization", name: "I-LEASE SRL", logo: { "@type": "ImageObject", url: "https://www.leasing-renting.com/ilease-emblem.png" } },
    mainEntityOfPage: `https://www.leasing-renting.com/blog/${article.slug}`,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.leasing-renting.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.leasing-renting.com/blog" },
      { "@type": "ListItem", position: 3, name: at.title, item: `https://www.leasing-renting.com/blog/${article.slug}` },
    ],
  };

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 2);
  const ctaLabel = { fr: "Demander une offre", nl: "Offerte aanvragen", en: "Request a quote" }[lang] || "Demander une offre";
  const alsoRead = { fr: "À lire aussi", nl: "Lees ook", en: "Also read" }[lang] || "À lire aussi";
  const ctaHead = { fr: "I-LEASE SRL · Depuis 2015", nl: "I-LEASE SRL · Sinds 2015", en: "I-LEASE SRL · Since 2015" }[lang] || "I-LEASE SRL · Depuis 2015";
  const ctaDesc = {
    fr: "Leasing, renting & LLD partout en Belgique. Réponse en 48h.",
    nl: "Leasing, renting & LLD overal in België. Antwoord binnen 48u.",
    en: "Leasing, renting & LTR throughout Belgium. Response within 48h.",
  }[lang] || "Leasing, renting & LLD partout en Belgique. Réponse en 48h.";

  return (
    <div className="min-h-screen" data-testid="blog-article">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <header className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo showEmblem={false} /></Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <BlogLangSwitcher />
            <Link to="/blog" className="text-sm text-muted-foreground hover:text-white flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Blog</Link>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <span className="text-[11px] uppercase tracking-wider bg-accent text-white rounded-full px-3 py-1">{at.category}</span>
        <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold leading-tight" data-testid="article-title">{at.title}</h1>
        <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
          <span>{new Date(article.date).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" })}</span>
          <span>·</span><span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {article.readTime}</span>
        </div>

        <motion.img initial={{ opacity: 0 }} animate={{ opacity: 1 }} src={article.cover} alt={at.title}
          className="mt-8 w-full h-64 sm:h-80 object-cover rounded-2xl border border-white/10" />

        <div className="prose-article mt-10">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{at.content}</ReactMarkdown>
        </div>

        <div className="mt-12 rounded-2xl glow-border glass p-7 text-center accent-glow">
          <h3 className="font-display text-xl font-bold">{ctaHead}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{ctaDesc}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button onClick={() => { window.location.href = "/#quote"; }} className="rounded-full bg-white text-black hover:bg-accent hover:text-white transition-colors">
              {ctaLabel} <ArrowRight className="ml-2 w-4 h-4" strokeWidth={2} />
            </Button>
            <a href="tel:+3267870553" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm hover:border-accent transition-colors">
              <Phone className="w-4 h-4 text-accent" strokeWidth={1.5} /> 067/87.05.53
            </a>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <h3 className="font-display text-lg font-bold mb-5">{alsoRead}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((a) => {
                const rt = trArticle(a, lang);
                return (
                <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`related-${a.slug}`}
                  className="group flex gap-4 rounded-xl border border-white/10 bg-card p-4 hover:border-accent/40 transition-colors">
                  <img src={a.cover} alt={rt.title} className="w-20 h-20 object-cover rounded-lg shrink-0" />
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-accent">{rt.category}</span>
                    <p className="font-display font-semibold text-sm leading-snug group-hover:text-accent transition-colors">{rt.title}</p>
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}
